import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { sitesService, licenseService, getErrorMessage } from '../services/api';
import { 
  FileText, Download, Users, CreditCard, Calendar,
  Building2, Loader2, AlertCircle, Printer
} from 'lucide-react';

export default function LicenseDistributionPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [distributionData, setDistributionData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Site Manager ise sadece kendi sitesi
  useEffect(() => {
    const fetchSites = async () => {
      try {
        if (user?.role === 'site_manager' && user?.site_id) {
          setSelectedSite(user.site_id);
          setLoading(false);
        } else {
          const sitesData = await sitesService.list();
          setSites(sitesData);
          if (sitesData.length > 0) {
            setSelectedSite(sitesData[0].id);
          }
        }
      } catch (err) {
        setError('Siteler yüklenemedi');
        setLoading(false);
      }
    };
    fetchSites();
  }, [user]);

  // Dağıtım verilerini getir
  useEffect(() => {
    const fetchDistribution = async () => {
      if (!selectedSite) return;
      
      setLoading(true);
      setError('');
      
      try {
        const data = await licenseService.getDistribution(selectedSite, selectedMonth, selectedYear);
        setDistributionData(data);
      } catch (err) {
        setError(getErrorMessage(err));
        setDistributionData(null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchDistribution();
  }, [selectedSite, selectedMonth, selectedYear]);

  // PDF İndir
  const handleDownloadPDF = () => {
    if (!distributionData) return;
    
    // Basit PDF oluştur (print dialog)
    const printContent = `
      <html>
        <head>
          <title>Lisans Bedeli Dağıtımı - ${distributionData.site_name}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { text-align: center; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
            th { background-color: #f5f5f5; }
            .total { font-weight: bold; background-color: #e8f5e9; }
            .header-info { margin-bottom: 20px; }
            .header-info p { margin: 5px 0; }
          </style>
        </head>
        <body>
          <h1>Lisans Bedeli Dağıtımı</h1>
          <div class="header-info">
            <p><strong>Site:</strong> ${distributionData.site_name}</p>
            <p><strong>Dönem:</strong> ${getMonthName(distributionData.period_month)} ${distributionData.period_year}</p>
            <p><strong>Toplam Lisans Bedeli:</strong> ${distributionData.total_license_fee?.toFixed(2)} ₺</p>
            <p><strong>Kullanıcı Sayısı:</strong> ${distributionData.user_count}</p>
            <p><strong>Kişi Başı Tutar:</strong> ${distributionData.per_user_amount?.toFixed(2)} ₺</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Kullanıcı</th>
                <th>E-posta</th>
                <th>RFID Kart</th>
                <th>Tutar</th>
              </tr>
            </thead>
            <tbody>
              ${distributionData.users.map((u, i) => `
                <tr>
                  <td>${i + 1}</td>
                  <td>${u.full_name}</td>
                  <td>${u.email}</td>
                  <td>${u.card_number}</td>
                  <td>${u.amount?.toFixed(2)} ₺</td>
                </tr>
              `).join('')}
              <tr class="total">
                <td colspan="4">TOPLAM</td>
                <td>${distributionData.total_license_fee?.toFixed(2)} ₺</td>
              </tr>
            </tbody>
          </table>
          <p style="margin-top: 20px; font-size: 12px; color: #666;">
            Oluşturulma Tarihi: ${new Date().toLocaleString('tr-TR')}
          </p>
        </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  // Excel İndir
  const handleDownloadExcel = () => {
    if (!distributionData) return;
    
    // CSV formatında indir
    const headers = ['#', 'Kullanıcı', 'E-posta', 'RFID Kart', 'Tutar (₺)'];
    const rows = distributionData.users.map((u, i) => [
      i + 1,
      u.full_name,
      u.email,
      u.card_number,
      u.amount?.toFixed(2)
    ]);
    
    // Toplam satırı ekle
    rows.push(['', 'TOPLAM', '', '', distributionData.total_license_fee?.toFixed(2)]);
    
    const csvContent = [
      `Site: ${distributionData.site_name}`,
      `Dönem: ${getMonthName(distributionData.period_month)} ${distributionData.period_year}`,
      '',
      headers.join(';'),
      ...rows.map(r => r.join(';'))
    ].join('\n');
    
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `lisans_dagitimi_${distributionData.period}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const getMonthName = (month) => {
    const months = ['', 'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
                    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
    return months[month] || '';
  };

  // Bireysel müşteri kontrolü - lisans dağıtım sayfası gizli
  if (user?.role === 'site_manager') {
    // Site manager'ın sitesinde sadece 1 kullanıcı varsa bu sayfaya gerek yok
    // Ama bunu backend'den kontrol etmek daha doğru olur
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
          <CreditCard className="h-7 w-7 text-blue-500" />
          Lisans Bedeli Dağıtımı
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          Aylık lisans bedelini RFID kartı olan kullanıcılara eşit dağıtın
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Site Seçimi (Super Admin için) */}
          {user?.role === 'super_admin' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                <Building2 className="h-4 w-4 inline mr-1" />
                Site
              </label>
              <select
                value={selectedSite}
                onChange={(e) => setSelectedSite(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              >
                {sites.map((site) => (
                  <option key={site.id} value={site.id}>{site.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Ay Seçimi */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              <Calendar className="h-4 w-4 inline mr-1" />
              Ay
            </label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
            >
              {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                <option key={m} value={m}>{getMonthName(m)}</option>
              ))}
            </select>
          </div>

          {/* Yıl Seçimi */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Yıl
            </label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
            >
              {[2024, 2025, 2026, 2027].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      )}

      {/* Distribution Data */}
      {!loading && distributionData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 dark:bg-blue-500/10 rounded-xl p-4 border border-blue-200 dark:border-blue-500/20">
              <p className="text-sm text-blue-600 dark:text-blue-400">Toplam Lisans Bedeli</p>
              <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                {distributionData.total_license_fee?.toFixed(2)} ₺
              </p>
            </div>
            <div className="bg-green-50 dark:bg-green-500/10 rounded-xl p-4 border border-green-200 dark:border-green-500/20">
              <p className="text-sm text-green-600 dark:text-green-400">RFID Kartlı Kullanıcı</p>
              <p className="text-2xl font-bold text-green-700 dark:text-green-300">
                {distributionData.user_count} kişi
              </p>
            </div>
            <div className="bg-purple-50 dark:bg-purple-500/10 rounded-xl p-4 border border-purple-200 dark:border-purple-500/20">
              <p className="text-sm text-purple-600 dark:text-purple-400">Kişi Başı Tutar</p>
              <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">
                {distributionData.per_user_amount?.toFixed(2)} ₺
              </p>
            </div>
            <div className="bg-amber-50 dark:bg-amber-500/10 rounded-xl p-4 border border-amber-200 dark:border-amber-500/20">
              <p className="text-sm text-amber-600 dark:text-amber-400">Dönem</p>
              <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">
                {getMonthName(distributionData.period_month)} {distributionData.period_year}
              </p>
            </div>
          </div>

          {/* User List */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                <Users className="h-5 w-5" />
                Kullanıcı Dağılımı
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={handleDownloadPDF}
                  className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                >
                  <FileText className="h-4 w-4" />
                  PDF
                </button>
                <button
                  onClick={handleDownloadExcel}
                  className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                >
                  <Download className="h-4 w-4" />
                  Excel
                </button>
                <button
                  onClick={handleDownloadPDF}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-500 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  <Printer className="h-4 w-4" />
                  Yazdır
                </button>
              </div>
            </div>

            {distributionData.users.length === 0 ? (
              <div className="p-8 text-center text-slate-500 dark:text-slate-400">
                <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Bu sitede RFID kartı olan kullanıcı bulunamadı</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-slate-700/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">#</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Kullanıcı</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">E-posta</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">RFID Kart</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Tutar</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                    {distributionData.users.map((user, index) => (
                      <tr key={user.user_id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30">
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">{index + 1}</td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-900 dark:text-white">{user.full_name}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">{user.email}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 rounded text-sm font-mono">
                            {user.card_number}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-slate-900 dark:text-white">
                          {user.amount?.toFixed(2)} ₺
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-green-50 dark:bg-green-500/10">
                    <tr>
                      <td colSpan="4" className="px-4 py-3 font-bold text-slate-900 dark:text-white">
                        TOPLAM
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-green-600 dark:text-green-400 text-lg">
                        {distributionData.total_license_fee?.toFixed(2)} ₺
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* Info Note */}
          <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-500/10 rounded-xl border border-blue-200 dark:border-blue-500/20">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              <strong>Not:</strong> Lisans bedeli, RFID kartı tanımlanmış tüm kullanıcılara eşit olarak dağıtılır. 
              Kartı olmayan kullanıcılar bu listeye dahil edilmez.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
